<template>
    <AppModal ref="filtersModal" :title="translate('labels.table_filters')">
        <div class="modal-body">
           <table class="table table-bordered table-fixed">
                <thead>
                    <tr>
                        <th width="50%">{{ $t('labels.field') }}</th>
                        <th width="50%">{{ $t('labels.action') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="field in fields">
                        <tr>
                            <td>{{ field.label }}</td>
                            <td>
                                <AppSelect
                                    :options="getActionOptionsByField(field.key)"
                                    :model-value="field.selectedAction?.key"
                                    :selected-value="field.selectedAction?.label"
                                    @update:model-value="selectAction(field, $event)"
                                    option-key="key"
                                    option-value="label"
                                    can-be-empty
                                />
                            </td>
                        </tr>
                        <tr v-if="field.selectedAction && (!['present', 'missing'].includes(field.selectedAction.key) && !['has_product', 'source_id_is_filled', 'second_source_id_is_filled'].includes(field.key) || field.key === 'tag')">
                            <td v-if="['export_enabled', 'is_auto_price_update_enabled', 'is_auto_balance_update_enabled'].includes(field.key)" colspan="2">
                                <AppCheckbox
                                    v-model="field.value"
                                />
                            </td>
                            <td v-else-if="['category_uuids'].includes(field.key)" colspan="2">
                                <MultiAutoComplete
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/categories`"
                                />
                            </td>
                            <td v-else-if="['tag'].includes(field.key)" colspan="2">
                                <MultiAutoComplete
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    url="/integration-product-tags"
                                />
                            </td>
                            <td v-else-if="['sku', 'manufacturer'].includes(field.key) || field.key.startsWith('name_') || field.key.startsWith('description_') || field.key.startsWith('seo_')" colspan="2" >
                                <div class="row" v-if="['coincidence'].includes(field.selectedAction.key)">
                                    <div class="col-md-6">
                                        <AppSelect
                                            :options="[
                                                { key: 'contains', label: $t('labels.partial')},
                                                { key: 'starts_with', label: $t('labels.from_the_beginning')},
                                                { key: 'ends_with', label: $t('labels.from_the_end')}
                                            ]"
                                            :model-value="field.value?.param?.key"
                                            :selected-value="field.value?.param?.label"
                                            @update:model-value="field.value.param = $event"
                                            option-key="key"
                                            option-value="label"
                                        />
                                    </div>
                                    <div class="col-md-6">
                                        <Input
                                            v-model="field.value.string"
                                        />
                                    </div>
                                </div>
                                <AppSelect
                                    v-else
                                    :options="languageOptions"
                                    :model-value="field.value?.param?.key"
                                    :selected-value="field.value?.param?.label"
                                    :selected-values="[extractLanguageAliasFromString(field.key)]"
                                    @update:model-value="field.value.param = $event"
                                    option-key="key"
                                    option-value="label"
                                />
                            </td>
                            <template v-else-if="['created_at'].includes(field.key)">
                                <td v-if="field.selectedAction.key === 'from'" colspan="2">
                                    <DatePicker
                                        v-model="field.value.from"
                                        :with-time="true"
                                    />
                                </td>
                                <td v-else-if="field.selectedAction.key === 'to'" colspan="2">
                                    <DatePicker
                                        v-model="field.value.to"
                                        :with-time="true"
                                    />
                                </td>
                                <td colspan="2" v-else-if="field.selectedAction.key === 'range'">
                                    <div class="row align-items-center">
                                        <div class="col-lg-5">
                                            <DatePicker
                                                v-model="field.value.from"
                                                :with-time="true"
                                            />
                                        </div>
                                        <div class="col-lg-2 text-center">
                                            {{ $t('labels.to') }}
                                        </div>
                                        <div class="col-lg-5">
                                            <DatePicker
                                                v-model="field.value.to"
                                                :with-time="true"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </template>
                            <template v-else-if="['balance'].includes(field.key)">
                                <td v-if="field.selectedAction.key === 'range'" colspan="2">
                                    <div class="row align-items-center">
                                        <div class="col-lg-5">
                                            <Input
                                                v-model="field.value.balance_from"
                                                type="number"
                                                :placeholder="$t('labels.from')"
                                            />
                                        </div>
                                        <div class="col-lg-2 text-center">
                                            {{ $t('labels.to') }}
                                        </div>
                                        <div class="col-lg-5">
                                            <Input
                                                v-model="field.value.balance_to"
                                                type="number"
                                                :placeholder="$t('labels.to')"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </template>
                            <template v-else-if="priceFieldKeys.includes(field.key)">
                                <td v-if="field.selectedAction.key === 'range'" colspan="2">
                                    <div class="row align-items-center">
                                        <div class="col-lg-5">
                                            <Input
                                                v-model="field.value.from"
                                                type="number"
                                                :placeholder="$t('labels.from')"
                                            />
                                        </div>
                                        <div class="col-lg-2 text-center">
                                            {{ $t('labels.to') }}
                                        </div>
                                        <div class="col-lg-5">
                                            <Input
                                                v-model="field.value.to"
                                                type="number"
                                                :placeholder="$t('labels.to')"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </template>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <div class="modal-footer">
            <AppButton size="sm" @click="apply" color="teal">{{ translate('buttons.apply') }}</AppButton>
        </div>
    </AppModal>
</template>
<script>
import FormGroup from '../../../../../../components/new/form/group/FormGroup.vue';
import AppSelect from '../../../../../../components/new/form/select/AppSelect.vue';
import MultiAutoComplete from '../../../../../../components/new/form/autocomplete/MultiAutoComplete.vue';
import AppCheckbox from '../../../../../../components/new/form/checkbox/AppCheckbox.vue';
import Input from '../../../../../../components/new/form/Input.vue';
import DatePicker from '../../../../../../components/new/form/datePiker/DatePicker.vue';
import AppModal from '../../../../../../components/new/modal/AppModal.vue';
import AppButton from '../../../../../../components/new/button/AppButton.vue';

export default {
    name: "ProductsFiltersModal",
    components: {
        FormGroup, AppSelect, MultiAutoComplete, AppCheckbox, Input, DatePicker, AppModal,
        AppButton
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    emits: ['update:filters'],
    data() {
        return {
            fields: [
                { key: 'has_product', label: this.$t('table.titles.internal_id'), selectedAction: null, value: null },
                { key: 'source_id_is_filled', label: this.$t('table.titles.source_id'), selectedAction: null, value: null },
                { key: 'second_source_id_is_filled', label: this.$t('table.titles.second_source_id'), selectedAction: null, value: null },
                { key: 'export_enabled', label: this.$t('labels.exportEnabled'), selectedAction: null, value: null },
                { key: 'category_uuids', label: this.$t('labels.categories'), selectedAction: null, value: null },
                ...(this.integration.language_aliases || []).map((lang) => ({
                    key: `name_${lang}`,
                    label: this.$t(`table.titles.name`) + ` (${this.$t(`shortLang.${lang}`)})`,
                    selectedAction: null,
                    value: null
                })),
                ...(this.integration.language_aliases || []).map((lang) => ({
                    key: `description_${lang}`,
                    label: this.$t(`table.titles.description`) + ` (${this.$t(`shortLang.${lang}`)})`,
                    selectedAction: null,
                    value: null
                })),
                ...(this.integration.language_aliases || []).map((lang) => ({
                    key: `seo_${lang}`,
                    label: this.$t(`table.titles.key_words`) + ` (${this.$t(`shortLang.${lang}`)})`,
                    selectedAction: null,
                    value: null
                })),
                { key: 'sku', label: this.$t('table.titles.sku'), selectedAction: null, value: null },
                { key: 'tag', label: this.$t('labels.tags'), selectedAction: null, value: null},
                { key: 'created_at', label: this.$t('table.titles.created_at'), selectedAction: null, value: null },
                { key: 'is_auto_price_update_enabled', label: this.$t('labels.auto_price_update_enabled'), selectedAction: null, value: null},
                { key: 'is_auto_balance_update_enabled', label: this.$t('labels.auto_balance_update_enabled'), selectedAction: null, value: null},
                { key: 'balance', label: this.$t('table.titles.balance'), selectedAction: null, value: null },
                { key: 'price', label: this.$t('table.titles.price'), selectedAction: null, value: null },
                { key: 'sale_price', label: this.$t('table.titles.sale_price'), selectedAction: null, value: null },
                { key: 'promo_price', label: this.$t('table.titles.promo_price'), selectedAction: null, value: null },
                { key: 'manufacturer', label: this.$t('table.titles.manufacturer'), selectedAction: null, value: null }
            ],
            priceFieldKeys: ['price', 'sale_price', 'promo_price'],
            languageOptions: this.integration.language_aliases?.map(lang => ({
                key: lang,
                label: this.$t(`languages.${lang}`)
            })) || []
        }
    },
    methods: {
        show() {
            this.$refs.filtersModal.show();
        },
        clear() {
            this.fields.forEach(field => {
                field.selectedAction = null;
                field.value = null;
            });
            this.$emit('update:filters', {}, 0);
        },
        extractLanguageAliasFromString(str) {
            const regex = /_(\w{2})$/;
            const match = str.match(regex);
            return match ? match[1] : null;
        },
        getActionOptionsByField(field) {
            let options = [];

            if (field === 'has_product' || field === 'source_id_is_filled' || field === 'second_source_id_is_filled' || field === 'tag') {
                options = [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                ];
            }

            if (field === 'export_enabled' || field === 'category_uuids' || field === 'is_auto_price_update_enabled' || field === 'is_auto_balance_update_enabled') {
                options = [
                    { key: 'correlate', label: this.$t('labels.correlate') },
                ]
            }

            if (field === 'sku') {
                options = [
                    { key: 'coincidence', label: this.$t('labels.coincidence') }
                ]
            }

            if (field === 'created_at') {
                options = [
                    { key: 'from', label: this.$t('labels.from') },
                    { key: 'to', label: this.$t('labels.to') },
                    { key: 'range', label: this.$t('labels.range') },
                ]
            }

            if (field.startsWith('name_') || field.startsWith('description_') || field.startsWith('seo_')) {
                options = [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                    { key: 'coincidence', label: this.$t('labels.coincidence') },
                    { key: 'conformity', label: this.$t('labels.conformity') },
                ]
            }

            if (this.priceFieldKeys.includes(field)) {
                options = [
                    { key: 'range', label: this.$t('labels.range') },
                ]
            }

            if (field === 'manufacturer') {
                options = [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                    { key: 'coincidence', label: this.$t('labels.coincidence') },
                ]
            }

            if (field === 'balance') {
                options = [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                    { key: 'range', label: this.$t('labels.range') },
                ]
            }

            return options;
        },
        selectAction(field, action) {
            if (action?.key === 'coincidence') {
                field.value = { param: { key: 'contains', label: this.$t('labels.partial')}, string: null };
            } else if (field.key === 'created_at') {
                if (action?.key === 'from') {
                    field.value = { from: null };
                } else if (action?.key === 'to') {
                    field.value = { to: null };
                } else if (action?.key === 'range') {
                    field.value = { from: null, to: null };
                }
            } else if (action?.key === 'conformity') {
                field.value = { param: null };
            } else if (field.key === 'balance') {
                if (action?.key === 'present') {
                    field.value = {
                        balance_from: 1
                    }
                } else if (action?.key === 'missing') {
                    field.value = {
                        balance_to: 0
                    }
                } else if (action?.key === 'range') {
                    field.value = {
                        balance_from: null,
                        balance_to: null
                    }
                }
            } else if (this.priceFieldKeys.includes(field.key) && action?.key === 'range') {
                field.value = { from: null, to: null };
            } else {
                field.value = null;
            }

            field.selectedAction = action;
        },
        apply() {
            this.$emit('update:filters')
            this.$refs.filtersModal.hide()
        },
    }
}
</script>
